/**
 * Calendar Component
 * A reusable calendar picker component
 */

import React, { useState } from "react";
import { useResources } from "../../hooks/useResources";
import "./Calendar.scss";

interface CalendarProps {
  onDateSelect: (date: Date) => void;
  onClear?: () => void;
  /** When provided, the calendar opens on this date's month/year */
  initialDate?: Date | null;
  /** Maximum selectable date. Dates after this will be disabled */
  maxDate?: Date;
}

const Calendar: React.FC<CalendarProps> = ({ onDateSelect, onClear, initialDate, maxDate }) => {
  const { getResourceValue } = useResources();
  const fallback = new Date();
  const dateToShow = initialDate && !isNaN(initialDate.getTime()) ? initialDate : fallback;
  const [currentMonth, setCurrentMonth] = useState(dateToShow.getMonth());
  const [currentYear, setCurrentYear] = useState(dateToShow.getFullYear());

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const handleNextMonth = () => {
    // Prevent navigating to months beyond maxDate
    if (maxDate) {
      const nextMonth = currentMonth === 11 ? 0 : currentMonth + 1;
      const nextYear = currentMonth === 11 ? currentYear + 1 : currentYear;
      
      // Check if the next month/year would be after maxDate
      if (nextYear > maxDate.getFullYear() || 
          (nextYear === maxDate.getFullYear() && nextMonth > maxDate.getMonth())) {
        return;
      }
    }
    
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const handleDateClick = (day: number) => {
    const date = new Date(currentYear, currentMonth, day);
    
    // Prevent selecting dates after maxDate
    if (maxDate && date > maxDate) {
      return;
    }
    
    onDateSelect(date);
  };

  const handleTodayClick = () => {
    const today = new Date();
    setCurrentMonth(today.getMonth());
    setCurrentYear(today.getFullYear());
    onDateSelect(today);
  };

  const handleClearClick = () => {
    if (onClear) {
      onClear();
    }
  };

  const renderCalendarDays = () => {
    const firstDay = new Date(currentYear, currentMonth, 1).getDay();
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
    const selected = dateToShow;

    const isSelectedDate = (day: number) =>
      selected &&
      day === selected.getDate() &&
      currentMonth === selected.getMonth() &&
      currentYear === selected.getFullYear();

    const isDisabledDate = (day: number) => {
      if (!maxDate) return false;
      const date = new Date(currentYear, currentMonth, day);
      return date > maxDate;
    };

    const days = [];

    // Empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="calendar__day calendar__day--empty"></div>);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const isSelected = isSelectedDate(day);
      const isDisabled = isDisabledDate(day);

      days.push(
        <div
          key={day}
          className={`calendar__day ${isSelected ? "calendar__day--selected" : ""} ${isDisabled ? "calendar__day--disabled" : ""}`}
          onClick={() => !isDisabled && handleDateClick(day)}
        >
          {day}
        </div>
      );
    }

    return days;
  };

  return (
    <div className="calendar">
      <div className="calendar__header">
        <button 
          className="calendar__nav-btn" 
          onClick={handlePrevMonth}
          type="button"
        >
          <i className="f-icon chevron-left-icon"></i>
        </button>
        <div className="calendar__month">
          {monthNames[currentMonth]} {currentYear}
        </div>
        <button 
          className="calendar__nav-btn" 
          onClick={handleNextMonth}
          type="button"
        >
          <i className="f-icon chevron-right-icon"></i>
        </button>
      </div>

      <div className="calendar__weekdays">
        <div>S</div>
        <div>M</div>
        <div>T</div>
        <div>W</div>
        <div>T</div>
        <div>F</div>
        <div>S</div>
      </div>

      <div className="calendar__days">
        {renderCalendarDays()}
      </div>

      <div className="calendar__footer">
        <button 
          className="calendar__btn calendar__btn--clear" 
          onClick={handleClearClick}
          type="button"
        >
          {getResourceValue("clear", "Clear")}
        </button>
        <button 
          className="calendar__btn calendar__btn--today" 
          onClick={handleTodayClick}
          type="button"
        >
          {getResourceValue("today", "Today")}
        </button>
      </div>
    </div>
  );
};

export default Calendar;
